import { usePage } from "@inertiajs/react"

import type { MenuGridCategory, MenuGridItem } from "@/components/features/menu/menu-grid"
import { StaffLayout } from "@/components/layouts/staff-layout"
import {
  DISCOUNTS_FEATURE,
  type AppliedDiscount,
  type DiscountEligibility,
  type DiscountKindOption,
  type DiscountReasonOption,
} from "@/features/staff/discounts"
import {
  REFUNDS_FEATURE,
  type IssuedRefund,
  type RefundEligibility,
  type RefundReasonOption,
} from "@/features/staff/refunds"
import type { CancellationReasonOption } from "@/features/staff/cancellations"
import type { ReopenReasonOption } from "@/features/staff/reopenings"
import { TableDetailWorkspace, type TableDiner, type TableReservation } from "@/features/staff/table-detail-workspace"
import { TablesOverview, type OverviewTable } from "@/features/staff/tables-overview"
import { DYNAMIC_INVITES_FEATURE } from "@/features/staff/table-status"
import type { StaffEntryRequest } from "@/features/staff/entry-request-panel"

type TableAction = {
  label: string
  value: string
  disabled: boolean
  primary: boolean
  kind: "navigate" | "event" | "invite"
  href?: string
  event?: string
  tableExternalId?: string
}

type ActiveInvite = {
  id: number
  token: string
  expiresAt: string
  maxUses: number
  usedCount: number
  url: string
}

type TableAccountOrder = {
  id: number
  name: string
  quantity: number
  status: string
  statusLabel: string
  serviceType: string
  serviceTypeLabel: string
  lineTotal: string
  // What the line cost before anything came off it, what came off, and the live
  // discount row. `lineTotal` is the charged figure now, so `grossLineTotal` is
  // the only place the original price survives.
  grossLineTotal?: string
  discountAmount?: string
  discount?: AppliedDiscount | null
  discountEligibility?: DiscountEligibility | null
  currency: string
  notes: string | null
  customerName: string
  updatedAt: string
  billId: number
  billLabel: string
  billStatus: string
  billStatusLabel: string
}

type TableAccountBill = {
  id: number
  label: string
  status: string
  statusLabel: string
  canClose: boolean
  pendingPaymentAttempt: {
    id: number
    status: string
    paymentMethod: string
    totalAmount: string
    selectedOrderIds: number[]
    payerName?: string | null
    isShare?: boolean
    createdAt?: string
  } | null
  // A bill settles in parallel, so the singular attempt above no longer
  // describes the table. These three carry the full picture.
  pendingPaymentAttempts: {
    id: number
    status: string
    paymentMethod: string
    totalAmount: string
    selectedOrderIds: number[]
    payerName?: string | null
    isShare?: boolean
    createdAt?: string
  }[]
  settledPayments: {
    id: number
    paymentMethod: string
    payerName: string | null
    subtotal: string
    tipAmount: string
    totalAmount: string
    isShare: boolean
    completedAt: string | null
    // Whether this charge can still be given back, and what already was.
    refundEligibility?: RefundEligibility | null
    refunds?: IssuedRefund[]
  }[]
  split: {
    id: number
    payerCount: number
    setTotal: string
    collected: string
    outstanding: string
    payersRemaining: number
    settling: boolean
  } | null
  allowedActions: {
    canClose: boolean
    canCreatePaymentAttempt: boolean
    canReopen: boolean
    canCancel: boolean
    canResolvePendingAttempt: boolean
    canCancelSplit: boolean
    canApplyDiscount?: boolean
    canAuthorizeDiscount?: boolean
  }
  ordersCount: number
  total: string
  // Everything taken off this bill (already inside `total`), and what is still
  // waiting for a manager to decide.
  discountTotal?: string
  pendingDiscounts?: AppliedDiscount[]
  discountEligibility?: DiscountEligibility | null
  currency: string
  orders: TableAccountOrder[]
}

type TableSummary = {
  id: number
  externalId: string
  name: string
  seats: number
  status: string
  statusLabel: string
  operationalStatus: OverviewTable["operationalStatus"]
  alert: boolean
  alertReasons: OverviewTable["alertReasons"]
  activeDinersCount: number
  diners: TableDiner[]
  activeOrdersCount: number
  activeBillsCount: number
  activeInvite: ActiveInvite | null
  entryRequests: StaffEntryRequest[]
  nextReservation: TableReservation | null
  accountWorkspace: {
    billCount: number
    orderCount: number
    visibleBills: TableAccountBill[]
  }
  actions: TableAction[]
}

type OrderFormPayload = {
  categories: MenuGridCategory[]
  orphanItems: MenuGridItem[]
  ordersErrors?: Array<Record<string, string | string[] | undefined>>
}

type LocationUser = {
  role: string
} | null

type Props = {
  tables: TableSummary[]
  orderForm: OrderFormPayload
  selectedTableExternalId?: string | null
  locationUser?: LocationUser
  // The closed refund catalog and the location's zone. Both belong to the
  // location rather than to a table, so they arrive once at page level.
  refundReasons?: RefundReasonOption[]
  // The discount catalogs, sent unconditionally for the same reason the refund
  // ones are: two static lists are cheaper than resolving the feature config to
  // decide whether to send them.
  discountReasons?: DiscountReasonOption[]
  discountKinds?: DiscountKindOption[]
  // The closed cancellation catalog, same unconditional-send reasoning as the
  // refund and discount ones above.
  cancellationReasons?: CancellationReasonOption[]
  reopenReasons?: ReopenReasonOption[]
  timeZone?: string
  // Shared by Staff::Location::BaseController for every screen in the domain.
  enabledFeatures?: string[]
}

// Who may ask for a discount. Wider than who may authorize one, and deliberately
// so: the person standing at the table is who the control exists for, and a
// waiter's discount does not take effect on its own — it lands pending. Mirrors
// `Location::User#can_apply_discounts?`.
const DISCOUNT_ROLES = ["manager", "waiter", "cashier"]

export default function StaffLocationTablesIndex() {
  const { tables, orderForm, selectedTableExternalId: selectedTableExternalIdFromServer, locationUser, refundReasons, discountReasons, discountKinds, cancellationReasons, reopenReasons, timeZone, enabledFeatures = [] } = usePage<Props>().props
  const isManager = locationUser?.role === "manager"
  // Both halves of what the endpoint enforces: the location has turned refunds
  // on, and this person may issue one (`Location::User#can_issue_refunds?` is
  // manager-only while the restaurant's refund policy is undefined). Offering
  // the button to a waiter would bounce them to the dashboard mid-service.
  const canRefund = isManager && enabledFeatures.includes(REFUNDS_FEATURE)
  // The same two halves for discounts, except that applying one is not
  // manager-only: the waiter asks and the manager decides, so the trigger has to
  // reach the floor. `Staff::Location::DiscountsController` enforces both
  // predicates regardless; this only keeps the control off a screen where
  // pressing it would bounce someone to the dashboard mid-service.
  const discountsOn = enabledFeatures.includes(DISCOUNTS_FEATURE)
  const canDiscount = discountsOn && DISCOUNT_ROLES.includes(locationUser?.role ?? "")
  const canAuthorizeDiscount = discountsOn && isManager
  // Off by default (#155 phase 4): the printed static QR is the MVP's door, so
  // every waiter-issued-QR control stays hidden until a manager opts in.
  const dynamicInvitesOn = enabledFeatures.includes(DYNAMIC_INVITES_FEATURE)
  const selectedTable = selectedTableExternalIdFromServer
    ? tables.find((table) => table.externalId === selectedTableExternalIdFromServer) ?? null
    : null

  if (selectedTable) {
    const switcherTables = tables.map((table) => ({
      id: table.id,
      externalId: table.externalId,
      name: table.name,
      status: table.status,
      statusLabel: table.statusLabel,
      operationalStatus: table.operationalStatus,
      alert: table.alert,
    }))

    return (
      <StaffLayout>
        <div className="h-full min-h-0">
          <TableDetailWorkspace
            key={selectedTable.id}
            table={{
              id: selectedTable.id,
              externalId: selectedTable.externalId,
              name: selectedTable.name,
              seats: selectedTable.seats,
              status: selectedTable.status,
              statusLabel: selectedTable.statusLabel,
              operationalStatus: selectedTable.operationalStatus,
              alert: selectedTable.alert,
              alertReasons: selectedTable.alertReasons,
              activeDinersCount: selectedTable.activeDinersCount,
              diners: selectedTable.diners,
              activeInvite: selectedTable.activeInvite,
              entryRequests: selectedTable.entryRequests,
              nextReservation: selectedTable.nextReservation,
            }}
            categories={orderForm.categories}
            orphanItems={orderForm.orphanItems}
            workspace={selectedTable.accountWorkspace}
            actions={selectedTable.actions}
            allTables={switcherTables}
            orderErrors={orderForm.ordersErrors}
            refundReasons={refundReasons}
            canRefund={canRefund}
            discountReasons={discountReasons}
            discountKinds={discountKinds}
            canDiscount={canDiscount}
            canAuthorizeDiscount={canAuthorizeDiscount}
            cancellationReasons={cancellationReasons}
            reopenReasons={reopenReasons}
            timeZone={timeZone}
            dynamicInvitesOn={dynamicInvitesOn}
          />
        </div>
      </StaffLayout>
    )
  }

  return (
    <StaffLayout>
      <TablesOverview tables={tables} isManager={isManager} />
    </StaffLayout>
  )
}
